// src/lib/learning/lessons.js

const ONBOARDING_ACHIEVEMENT_XP = {
  profile_gate_personal_complete: 25,
  profile_gate_business_complete: 75,
};

// Build canonical lesson slug from route params
export const slugFor = (questId, subquestId, levelKey) =>
  `q${questId}-${subquestId}-${levelKey}`;

/**
 * Find the existing lesson row by its canonical level key.
 */
export async function ensureLessonBySlug(supabase, { levelKey }) {
  const normalizedLevelKey = String(levelKey || "").trim().toUpperCase();

  if (!normalizedLevelKey) {
    throw new Error("Missing level key");
  }

  const { data, error } = await supabase
    .from("lessons")
    .select("id")
    .eq("level_key", normalizedLevelKey)
    .maybeSingle();

  if (error) throw error;

  if (!data?.id) {
    throw new Error(`Level ${normalizedLevelKey} is not registered in Supabase`);
  }

  return data.id;
}

/**
 * Idempotent progress writer for a user/lesson.
 * This function does not decide the XP amount.
 */
export async function upsertLessonProgress(
  supabase,
  { userId, lessonId, completed, xpEarned }
) {
  const now = new Date().toISOString();
  const passed = !!completed;

  const parsedXp = Number(xpEarned || 0);
  const award = passed && Number.isFinite(parsedXp)
    ? Math.max(0, parsedXp)
    : 0;

  const { error: insErr } = await supabase.from("lesson_progress").insert({
    user_id: userId,
    lesson_id: lessonId,
    completed: passed,
    completed_at: passed ? now : null,
    xp_earned: award,
  });

  if (insErr && insErr.code !== "23505" && insErr.code !== "409") {
    throw insErr;
  }

  const { data: row, error: selErr } = await supabase
    .from("lesson_progress")
    .select("id, completed, completed_at, xp_earned")
    .eq("user_id", userId)
    .eq("lesson_id", lessonId)
    .single();

  if (selErr) throw selErr;

  const existingXp = Number(row.xp_earned || 0);

  const merged = {
    completed: row.completed || passed,
    completed_at: row.completed_at || (passed ? now : null),
    xp_earned: Math.max(existingXp, award),
  };

  if (
    merged.completed !== row.completed ||
    merged.completed_at !== row.completed_at ||
    merged.xp_earned !== row.xp_earned
  ) {
    const { error: updErr } = await supabase
      .from("lesson_progress")
      .update(merged)
      .eq("user_id", userId)
      .eq("lesson_id", lessonId);

    if (updErr) throw updErr;
  }

  return merged;
}

/**
 * Complete a lesson without granting browser-calculated XP.
 */
export async function markLessonCompletedWithoutXp(supabase, { userId, lessonId }) {
  if (!userId || !lessonId) {
    throw new Error("A user and lesson are required to record completion");
  }
  return upsertLessonProgress(supabase, {
    userId,
    lessonId,
    completed: true,
    xpEarned: 0,
  });
}

/** Quest progress for a user based on lessons.quest_id */
export async function getQuestProgress(supabase, { userId, questId }) {
  let uid = userId;

  if (!uid) {
    const {
      data: { user },
      error: authErr,
    } = await supabase.auth.getUser();

    if (authErr) throw authErr;
    if (!user) throw new Error("Not authenticated");

    uid = user.id;
  }

  const { data: questLessons, error: lessonErr } = await supabase
    .from("lessons")
    .select("id")
    .eq("quest_id", String(questId));

  if (lessonErr) throw lessonErr;

  const lessonIds = (questLessons ?? []).map((lesson) => lesson.id);

  if (lessonIds.length === 0) {
    return { total: 0, completed: 0, percent: 0 };
  }

  const { data: progressRows, error: progErr } = await supabase
    .from("lesson_progress")
    .select("lesson_id, completed")
    .eq("user_id", uid)
    .in("lesson_id", lessonIds);

  if (progErr) throw progErr;

  const total = lessonIds.length;
  const completed = (progressRows ?? []).filter((row) => row.completed).length;
  const percent = total ? Math.round((completed / total) * 100) : 0;

  return { total, completed, percent };
}

/**
 * Legacy lesson XP within a quest.
 * Verified Action XP is not yet part of this quest-specific helper: the
 * existing user-scoped RPC supplies an all-level total, not a per-quest split.
 */
export async function getUserQuestXp(supabase, questId) {
  const {
    data: { user },
    error: authErr,
  } = await supabase.auth.getUser();

  if (authErr) throw authErr;
  if (!user) throw new Error("Not authenticated");

  const { data: questLessons, error: lessonErr } = await supabase
    .from("lessons")
    .select("id")
    .eq("quest_id", String(questId));

  if (lessonErr) throw lessonErr;

  const lessonIds = (questLessons ?? []).map((lesson) => lesson.id);

  if (lessonIds.length === 0) {
    return 0;
  }

  const { data: rows, error: progErr } = await supabase
    .from("lesson_progress")
    .select("xp_earned")
    .eq("user_id", user.id)
    .in("lesson_id", lessonIds);

  if (progErr) throw progErr;

  return (rows ?? []).reduce(
    (sum, row) => sum + Number(row.xp_earned || 0),
    0
  );
}

async function getUserOnboardingXp(supabase, userId) {
  const { data, error } = await supabase
    .from("user_achievements")
    .select("achievement_key, meta")
    .eq("user_id", userId)
    .in("achievement_key", Object.keys(ONBOARDING_ACHIEVEMENT_XP));

  if (error) throw error;

  return (data ?? []).reduce((sum, row) => {
    const fallback = ONBOARDING_ACHIEVEMENT_XP[row.achievement_key] || 0;
    const metaXp = Number(row?.meta?.xp);

    return sum + (Number.isFinite(metaXp) ? metaXp : fallback);
  }, 0);
}

/**
 * Read the learner's verified Action XP without accessing restricted tables.
 * The RPC was created as part of the Level Result A-XP integration.
 * The level key is required by the RPC, but its total_action_xp field
 * includes awards from ALL levels, not only L1.
 */
/**
 * Read verified Action XP from the learner's Supabase account.
 */
async function getUserVerifiedActionXp(supabase) {
  const { data, error } = await supabase.rpc(
    "get_my_learning_action_xp_summary",
    { p_level_key: "L1" }
  );

  if (error) {
    throw new Error(
      `Could not load verified Action XP: ${error.message}`
    );
  }

  const actionXp = Number(data?.total_action_xp);

  if (!Number.isFinite(actionXp) || actionXp < 0) {
    throw new Error(
      "Verified Action XP summary returned an invalid amount"
    );
  }

  return actionXp;
}

/**
 * Read verified Knowledge Check XP from the learner's Supabase account.
 */
async function getUserVerifiedKnowledgeXp(supabase) {
  const { data, error } = await supabase.rpc(
    "get_my_knowledge_xp_summary",
    { p_level_key: null }
  );

  if (error) {
    throw new Error(
      `Could not load verified Knowledge Check XP: ${error.message}`
    );
  }

  const knowledgeXp = Number(data?.total_knowledge_xp);

  if (!Number.isFinite(knowledgeXp) || knowledgeXp < 0) {
    throw new Error(
      "Verified Knowledge Check XP summary returned an invalid amount"
    );
  }

  return knowledgeXp;
}

/**
 * Current user's full XP:
 * legacy lesson XP + achievement XP +
 * verified Knowledge Check XP + verified Action XP.
 *
 * Historical overlap must be prevented by the backend cutover rules.
 */


export async function getUserTotalXp(supabase) {
  const {
    data: { user },
    error: authErr,
  } = await supabase.auth.getUser();

  if (authErr) throw authErr;
  if (!user) throw new Error("Not authenticated");

  const [
  { data: lessonXpRows, error: lessonXpErr },
  { data: achievementRows, error: achievementErr },
  verifiedKnowledgeXp,
  verifiedActionXp,
] = await Promise.all([
  supabase
    .from("lesson_progress")
    .select("xp_earned")
    .eq("user_id", user.id),

  supabase
    .from("user_achievements")
    .select("achievement_key, meta")
    .eq("user_id", user.id),

  getUserVerifiedKnowledgeXp(supabase),
  getUserVerifiedActionXp(supabase),
]);

  if (lessonXpErr) throw lessonXpErr;
  if (achievementErr) throw achievementErr;

  const lessonXp = (lessonXpRows ?? []).reduce(
    (sum, row) => sum + Number(row.xp_earned || 0),
    0
  );

  const achievementXp = (achievementRows ?? []).reduce((sum, row) => {
    const metaXp = Number(row?.meta?.xp);

    if (Number.isFinite(metaXp)) {
      return sum + metaXp;
    }

    const fallback = ONBOARDING_ACHIEVEMENT_XP[row.achievement_key] || 0;
    return sum + fallback;
  }, 0);

  return (
  lessonXp +
  achievementXp +
  verifiedKnowledgeXp +
  verifiedActionXp
);
}

/** Kept for backwards compatibility with old imports. */
export const QUIZ_TOTAL_CAP = 15;

/** Badge helper */
export function badgeForXp(totalXp) {
  if (totalXp >= 600) return { tier: 3, label: "Legend" };
  if (totalXp >= 300) return { tier: 2, label: "Pro" };
  if (totalXp >= 150) return { tier: 1, label: "Rising Star" };
  return { tier: 0, label: "Newcomer" };
}

export { checkAndAwardAchievementsOnResult } from "./achievements";